/**
 * Box Extrusion Handler
 *
 * Shared logic for creating rectangular extrusions (box pull-outs) from prompt lines.
 * Used by both AutomaticLineMode and ManualLineMode. Handles:
 * - Computing density-snapped edge previews during drag
 * - Creating box prompts (split + arch) with density alignment on release
 */

import * as THREE from 'three';
import { LinePrompt } from './types';
import { DensitySnapper } from './DensitySnapper';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { PromptAligner } from './PromptAligner';

export interface BoxDragState {
  prompt: LinePrompt;
  startPoint: THREE.Vector3;
  side: 'positive' | 'negative';
  /** True when the drag started from an endpoint/intersection — enables dynamic edge switching */
  snappedToEndpoint?: boolean;
}

export interface BoxExtrusionResult {
  /** Prompts to add (split prompts + arch prompts) */
  newPrompts: LinePrompt[];
  /** Index of the original prompt to remove (-1 if not found) */
  originalPromptIndex: number;
}

export class BoxExtrusionHandler {
  constructor(
    private coordinateTransformer: CoordinateTransformer,
    private densitySnapper: DensitySnapper,
  ) {}

  /**
   * In-plane perpendicular to `dir`. With a base-plane frame: returns
   * `frame.normal × dir` (unit). On the world XY floor plane (normal =
   * +Z) this reduces to `(-dir.y, dir.x, 0)`. On a vertical wall plane
   * (normal = horizontal perpendicular) this returns the vertical
   * in-plane perpendicular along the wall. Fallback to the legacy XY
   * perp when no frame is set, preserving pre-frame behavior.
   *
   * Public so the LineTool modes can reuse it for their own in-plane
   * geometry (e.g. the bisector-side test in the prompt-line drag
   * handler) without duplicating the frame-or-XY fallback logic.
   */
  inPlanePerp(dir: THREE.Vector3): THREE.Vector3 {
    const frame = this.coordinateTransformer.getBasePlaneFrame();
    if (frame) {
      const perp = new THREE.Vector3().crossVectors(frame.normal, dir);
      if (perp.lengthSq() > 1e-9) return perp.normalize();
    }
    return new THREE.Vector3(-dir.y, dir.x, 0).normalize();
  }

  /**
   * Compute clamped box corners from a drag, ensuring the box stays within the
   * originating prompt line endpoints. Returns null if the drag is too small.
   */
  computeBoxCorners(
    startPoint: THREE.Vector3,
    currentWorldPoint: THREE.Vector3,
    prompt: LinePrompt,
  ): {
    corner1: THREE.Vector3;
    corner2: THREE.Vector3;
    corner3: THREE.Vector3;
    corner4: THREE.Vector3;
    promptDir: THREE.Vector3;
    perpDir: THREE.Vector3;
    sideLength: number;
    perpLength: number;
  } | null {
    const promptDir = prompt.direction.clone().normalize();
    const perpDir = this.inPlanePerp(promptDir);
    const corner1 = startPoint.clone();
    const diagonal = new THREE.Vector3().subVectors(currentWorldPoint, corner1);
    const alongPrompt = diagonal.dot(promptDir);
    const alongPerp = diagonal.dot(perpDir);

    if (Math.abs(alongPrompt) < 0.01 && Math.abs(alongPerp) < 0.01) return null;

    // Clamp along prompt direction so the box cannot exceed the prompt line endpoints
    const posLen = prompt.positiveLength ?? 1.5;
    const negLen = prompt.negativeLength ?? 1.5;
    const corner1Proj = corner1.clone().sub(prompt.worldPoint).dot(promptDir);
    const corner2Proj = corner1Proj + alongPrompt;
    const clampedCorner2Proj = Math.max(-negLen, Math.min(posLen, corner2Proj));
    const clampedAlongPrompt = clampedCorner2Proj - corner1Proj;

    const corner2 = corner1.clone().add(promptDir.clone().multiplyScalar(clampedAlongPrompt));
    const corner3 = corner1.clone().add(perpDir.clone().multiplyScalar(alongPerp));
    const corner4 = corner2.clone().add(perpDir.clone().multiplyScalar(alongPerp));

    return {
      corner1,
      corner2,
      corner3,
      corner4,
      promptDir,
      perpDir,
      sideLength: Math.abs(clampedAlongPrompt),
      perpLength: Math.abs(alongPerp),
    };
  }

  /**
   * Find the nearest prompt whose line passes through an endpoint, using geometry
   * rather than pre-computed connections (which can be stale after undo/redo).
   * For temporary prompts, only considers other temporary prompts as candidates.
   *
   * @param endpoint The point to search around
   * @param sourcePrompt The prompt that owns the endpoint (excluded from results)
   * @param allPrompts All prompts to search through
   * @param requireCollinearTo If provided, only return candidates whose direction is
   *   parallel to this direction (dot > 0.95). Used for collapsed-leg detection.
   */
  findNeighborAtEndpoint(endpoint: THREE.Vector3, sourcePrompt: LinePrompt, allPrompts: LinePrompt[], requireCollinearTo?: THREE.Vector3): LinePrompt | null {
    let bestNeighbor: LinePrompt | null = null;
    let bestDist = 0.15;
    for (const candidate of allPrompts) {
      if (candidate === sourcePrompt) continue;
      if (sourcePrompt.isTemporary && !candidate.isTemporary) continue;
      if (!sourcePrompt.isTemporary && candidate.isTemporary) continue;
      const toEndpoint = new THREE.Vector3().subVectors(endpoint, candidate.worldPoint);
      const candidateDir = candidate.direction.clone().normalize();
      // Perpendicular distance from `endpoint` to the candidate's infinite
      // line. Was a 2D XY cross (`toEndpoint.x * dir.y - toEndpoint.y *
      // dir.x`) — correct for room edits on a horizontal floor plane
      // (every direction has z=0), wrong on a vertical wall where
      // directions/endpoints have real Z and the XY shadow doesn't
      // reflect the true perp distance. The full 3D cross-magnitude
      // works on any plane: `|a × b| = |a| |b| sin θ`, so dividing by
      // |b|=1 (candidateDir is normalized) gives the perpendicular
      // component of `toEndpoint` in 3D.
      const perpDist = new THREE.Vector3().crossVectors(toEndpoint, candidateDir).length();
      if (perpDist < bestDist) {
        const projOnCandidate = toEndpoint.dot(candidateDir);
        const cPosLen = candidate.positiveLength ?? 1.5;
        const cNegLen = candidate.negativeLength ?? 1.5;
        if (projOnCandidate >= -(cNegLen + 0.1) && projOnCandidate <= cPosLen + 0.1) {
          if (requireCollinearTo) {
            const dot = Math.abs(requireCollinearTo.dot(candidateDir));
            if (dot <= 0.95) continue;
          }
          bestDist = perpDist;
          bestNeighbor = candidate;
        }
      }
    }
    return bestNeighbor;
  }

  /**
   * Compute density-snapped edges for the box extrusion preview.
   * Creates temporary prompts for each box edge, snaps them to density features,
   * and returns the snapped edge endpoints for green preview rendering.
   *
   * Edge order: [0] = edge 2→4 (leg), [1] = edge 4→3 (arch top), [2] = edge 3→1 (leg)
   */
  computeSnappedBoxEdges(
    corner1: THREE.Vector3,
    corner2: THREE.Vector3,
    corner3: THREE.Vector3,
    corner4: THREE.Vector3,
    promptDir: THREE.Vector3,
    perpDir: THREE.Vector3,
    sideLength: number,
    perpLength: number,
    densityMap: ImageData,
    positionMap: ImageData | null,
    floorHeight: number | null,
    prompt?: LinePrompt,
  ): Array<[THREE.Vector3, THREE.Vector3]> {
    // Snap each edge independently first
    const leg1 = this.snapEdge(corner2, corner4, perpLength, densityMap, positionMap, floorHeight);
    const arch = this.snapEdge(corner4, corner3, sideLength, densityMap, positionMap, floorHeight);
    const leg2 = this.snapEdge(corner3, corner1, perpLength, densityMap, positionMap, floorHeight);

    // When only one wall line exists, both legs snap to the same position.
    // Detect overlap: if snapped leg midpoints are closer together along promptDir
    // than half the original separation, they found the same wall.
    // Reset the leg that moved further from its original position.
    const origLeg1Mid = new THREE.Vector3().addVectors(corner2, corner4).multiplyScalar(0.5);
    const origLeg2Mid = new THREE.Vector3().addVectors(corner3, corner1).multiplyScalar(0.5);
    const leg1SnappedMid = new THREE.Vector3().addVectors(leg1[0], leg1[1]).multiplyScalar(0.5);
    const leg2SnappedMid = new THREE.Vector3().addVectors(leg2[0], leg2[1]).multiplyScalar(0.5);

    const snappedSeparation = Math.abs(leg1SnappedMid.dot(promptDir) - leg2SnappedMid.dot(promptDir));

    // If snapped legs are exactly on top of each other (< 0.02 world units apart),
    // they found the same wall — reset the one that was originally further from the target
    if (snappedSeparation < 0.005) {
      const leg1Dist = leg1SnappedMid.distanceTo(origLeg1Mid);
      const leg2Dist = leg2SnappedMid.distanceTo(origLeg2Mid);
      const edgeDir1 = new THREE.Vector3().subVectors(corner4, corner2).normalize();
      const edgeDir2 = new THREE.Vector3().subVectors(corner1, corner3).normalize();

      if (leg1Dist <= leg2Dist) {
        // Leg1 was closer to the wall — reset leg2 to user input
        const halfDir2 = edgeDir2.clone().multiplyScalar(perpLength / 2);
        leg2[0] = origLeg2Mid.clone().sub(halfDir2);
        leg2[1] = origLeg2Mid.clone().add(halfDir2);
      } else {
        // Leg2 was closer to the wall — reset leg1 to user input
        const halfDir1 = edgeDir1.clone().multiplyScalar(perpLength / 2);
        leg1[0] = origLeg1Mid.clone().sub(halfDir1);
        leg1[1] = origLeg1Mid.clone().add(halfDir1);
      }
    }

    // Determine which side of the originating line (corner1-corner2) the drag goes to.
    // The signed distance of corner4 from the base line gives us the expected sign.
    // All snapped edges must stay on this same side.
    const baseMidpoint = new THREE.Vector3().addVectors(corner1, corner2).multiplyScalar(0.5);
    const dragSign = Math.sign(new THREE.Vector3().subVectors(corner4, baseMidpoint).dot(perpDir));

    // Only clamp the arch — it must not cross back past the originating line.
    // Legs naturally span from base to far side, so their endpoints straddle the base line.
    this.clampEdgeToSide(arch, perpDir, baseMidpoint, dragSign);

    // Connect edges by computing intersections of adjacent perpendicular lines.
    // Each snapped edge defines a line (midpoint + direction). Since legs are perpDir
    // and arch is promptDir, they're perpendicular. The intersection gives shared corners.

    // Intersection of leg1 (perpDir) and arch (promptDir) → shared corner at "corner4" side
    const corner4Shared = this.intersectPerpendicularLines(leg1[0], perpDir, arch[0], promptDir);

    // Intersection of arch (promptDir) and leg2 (perpDir) → shared corner at "corner3" side
    const corner3Shared = this.intersectPerpendicularLines(arch[0], promptDir, leg2[0], perpDir);

    if (corner4Shared) {
      // Leg1 runs from its base (corner2 side) to the shared corner4
      leg1[1] = corner4Shared.clone();
      // Arch runs from shared corner4 to shared corner3 (or its original end)
      arch[0] = corner4Shared.clone();
    }
    if (corner3Shared) {
      arch[1] = corner3Shared.clone();
      // Leg2 runs from shared corner3 to its base (corner1 side)
      leg2[0] = corner3Shared.clone();
    }

    // Clamp legs so they don't extend beyond the originating prompt's endpoints.
    // Density snapping can shift legs along promptDir, pushing their base past the line ends.
    // Move the ENTIRE leg (both endpoints + shared arch corner) to maintain perpendicularity.
    if (prompt) {
      const posLen = prompt.positiveLength ?? 1.5;
      const negLen = prompt.negativeLength ?? 1.5;

      // Leg1: check base point (leg1[0]) projection along promptDir
      const leg1Proj = leg1[0].clone().sub(prompt.worldPoint).dot(promptDir);
      const leg1Clamped = Math.max(-negLen, Math.min(posLen, leg1Proj));
      const leg1Delta = leg1Clamped - leg1Proj;
      if (Math.abs(leg1Delta) > 1e-6) {
        leg1[0].addScaledVector(promptDir, leg1Delta);
        leg1[1].addScaledVector(promptDir, leg1Delta);
        arch[0].addScaledVector(promptDir, leg1Delta);
      }

      // Leg2: check base point (leg2[1]) projection along promptDir
      const leg2Proj = leg2[1].clone().sub(prompt.worldPoint).dot(promptDir);
      const leg2Clamped = Math.max(-negLen, Math.min(posLen, leg2Proj));
      const leg2Delta = leg2Clamped - leg2Proj;
      if (Math.abs(leg2Delta) > 1e-6) {
        leg2[0].addScaledVector(promptDir, leg2Delta);
        leg2[1].addScaledVector(promptDir, leg2Delta);
        arch[1].addScaledVector(promptDir, leg2Delta);
      }
    }

    return [leg1, arch, leg2];
  }

  /**
   * Clamp a snapped edge so both endpoints stay on the expected side of the originating line.
   * If an endpoint has crossed to the wrong side (signed distance along perpDir from baseMidpoint
   * has the wrong sign), it is projected back onto the base line.
   */
  private clampEdgeToSide(edge: [THREE.Vector3, THREE.Vector3], perpDir: THREE.Vector3, baseMidpoint: THREE.Vector3, dragSign: number): void {
    for (let i = 0; i < 2; i++) {
      const signedDist = new THREE.Vector3().subVectors(edge[i], baseMidpoint).dot(perpDir);
      if (Math.sign(signedDist) !== dragSign && Math.abs(signedDist) > 1e-6) {
        // Project point back onto the base line (remove the perpendicular component)
        edge[i].addScaledVector(perpDir, -signedDist);
      }
    }
  }

  /**
   * Compute the intersection point of two perpendicular lines in the
   * active base plane's (u, v). Delegates to `PromptAligner.intersect
   * InfiniteLines` with the transformer so the 2D intersection runs in
   * local coords (identity on the floor plane, wall-tangent on the wall
   * plane). Returns the intersection point in world coords, or null if
   * the lines are parallel in the plane.
   */
  private intersectPerpendicularLines(pointA: THREE.Vector3, dirA: THREE.Vector3, pointB: THREE.Vector3, dirB: THREE.Vector3): THREE.Vector3 | null {
    return PromptAligner.intersectInfiniteLines(pointA, dirA, pointB, dirB, this.coordinateTransformer);
  }

  /**
   * Create a box (rectangular extrusion) from a prompt line drag operation.
   * Returns the new prompts to add and the index of the original prompt to remove.
   * The caller is responsible for mutating its linePrompts array and calling updateShapePreview.
   *
   * @param drag The drag state (prompt, start point, side)
   * @param endWorldPoint Mouse release position in world space
   * @param allPrompts Current array of all line prompts (for connection analysis)
   * @param nextIdFn Callback that returns a unique prompt ID (e.g. `prompt_${this.nextPromptId++}`)
   * @param snappedEdges Pre-computed density-snapped edges from the drag preview, or null
   * @param densityMap Cached density map for alignment, or null
   * @param positionMap Cached position map for alignment, or null
   * @param floorHeight Floor height for Z alignment, or null
   * @param isTemporary Whether new prompts should be marked as temporary
   */
  createBoxFromDrag(
    drag: BoxDragState,
    endWorldPoint: THREE.Vector3,
    allPrompts: LinePrompt[],
    nextIdFn: () => string,
    snappedEdges: Array<[THREE.Vector3, THREE.Vector3]> | null,
    densityMap: ImageData | null,
    positionMap: ImageData | null,
    floorHeight: number | null,
    isTemporary?: boolean,
  ): BoxExtrusionResult | null {
    const { prompt, startPoint } = drag;

    const promptDir = prompt.direction.clone().normalize();
    const perpDir = this.inPlanePerp(promptDir);

    const corner1 = startPoint.clone();
    const corner4 = endWorldPoint.clone();

    const diagonal = new THREE.Vector3().subVectors(corner4, corner1);
    const alongPrompt = diagonal.dot(promptDir);
    const alongPerp = diagonal.dot(perpDir);

    if (Math.abs(alongPrompt) < 0.05 && Math.abs(alongPerp) < 0.05) {
      return null;
    }

    const corner2 = corner1.clone().add(promptDir.clone().multiplyScalar(alongPrompt));
    const corner3 = corner1.clone().add(perpDir.clone().multiplyScalar(alongPerp));

    const sideLength = Math.abs(alongPrompt);
    const perpLength = Math.abs(alongPerp);

    const originalIndex = allPrompts.indexOf(prompt);

    // Get the original line endpoints. Pre-fix this clamped both
    // endpoint Zs to `corner1.z` — a no-op on the floor plane (every
    // prompt has z=0 there and corner1 sits on the line) but
    // destructive on a vertical wall, where a vertical/diagonal prompt
    // has a real Z component along `promptDir`. Forcing z=corner1.z
    // collapses the endpoints onto a horizontal line through the drag
    // start and downstream neighbor lookup misses the real neighbors,
    // skip-leg decisions go wrong, and the box comes out malformed.
    const originalPosEnd = prompt.worldPoint.clone().add(promptDir.clone().multiplyScalar(prompt.positiveLength ?? 1.5));
    const originalNegEnd = prompt.worldPoint.clone().add(promptDir.clone().multiplyScalar(-(prompt.negativeLength ?? 1.5)));

    const corner1Proj = corner1.clone().sub(prompt.worldPoint).dot(promptDir);
    const corner2Proj = corner2.clone().sub(prompt.worldPoint).dot(promptDir);
    const posEndProj = prompt.positiveLength ?? 1.5;
    const negEndProj = -(prompt.negativeLength ?? 1.5);

    let minBoxProj = Math.min(corner1Proj, corner2Proj);
    let maxBoxProj = Math.max(corner1Proj, corner2Proj);
    const corner1IsMin = corner1Proj <= corner2Proj;

    // When snapped edges are available, derive box boundaries from the snapped leg
    // base positions so split prompts connect to the preview lines, not user input.
    // leg1[0] = base at corner2 side, leg2[1] = base at corner1 side
    if (snappedEdges && snappedEdges.length === 3) {
      const snappedLeg1BaseProj = snappedEdges[0][0].clone().sub(prompt.worldPoint).dot(promptDir);
      const snappedLeg2BaseProj = snappedEdges[2][1].clone().sub(prompt.worldPoint).dot(promptDir);
      minBoxProj = Math.min(snappedLeg1BaseProj, snappedLeg2BaseProj);
      maxBoxProj = Math.max(snappedLeg1BaseProj, snappedLeg2BaseProj);
    }

    // Find collinear neighbors at the prompt endpoints using geometry
    const negNeighbor = this.findNeighborAtEndpoint(originalNegEnd, prompt, allPrompts, perpDir);
    const posNeighbor = this.findNeighborAtEndpoint(originalPosEnd, prompt, allPrompts, perpDir);

    const skipNegSide = !!(negNeighbor && Math.abs(negEndProj - minBoxProj) < 0.1);

    const skipPosSide = !!(posNeighbor && Math.abs(posEndProj - maxBoxProj) < 0.1);

    const skipLegAtCorner1 = corner1IsMin ? skipNegSide : skipPosSide;
    const skipLegAtCorner2 = corner1IsMin ? skipPosSide : skipNegSide;

    const resolvedEdges = snappedEdges ?? null;
    const newPrompts: LinePrompt[] = [];

    // Split prompt: negEnd side
    if (negEndProj < minBoxProj - 0.05 && !skipNegSide) {
      const nearBoxPoint = prompt.worldPoint.clone().add(promptDir.clone().multiplyScalar(minBoxProj));
      const dist = Math.abs(minBoxProj - negEndProj);
      const midpoint = new THREE.Vector3().addVectors(originalNegEnd, nearBoxPoint).multiplyScalar(0.5);
      const splitPrompt: LinePrompt = {
        id: nextIdFn(),
        worldPoint: midpoint,
        direction: promptDir.clone(),
        screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
        positiveLength: dist / 2,
        negativeLength: dist / 2,
        isTemporary: isTemporary ? true : undefined,
      };
      if (!resolvedEdges) {
        this.alignSplitPrompt(splitPrompt, densityMap, positionMap, floorHeight);
      }
      newPrompts.push(splitPrompt);
    }

    // Split prompt: posEnd side
    if (posEndProj > maxBoxProj + 0.05 && !skipPosSide) {
      const farBoxPoint = prompt.worldPoint.clone().add(promptDir.clone().multiplyScalar(maxBoxProj));
      const dist = Math.abs(posEndProj - maxBoxProj);
      const midpoint = new THREE.Vector3().addVectors(farBoxPoint, originalPosEnd).multiplyScalar(0.5);
      const splitPrompt: LinePrompt = {
        id: nextIdFn(),
        worldPoint: midpoint,
        direction: promptDir.clone(),
        screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
        positiveLength: dist / 2,
        negativeLength: dist / 2,
        isTemporary: isTemporary ? true : undefined,
      };
      if (!resolvedEdges) {
        this.alignSplitPrompt(splitPrompt, densityMap, positionMap, floorHeight);
      }
      newPrompts.push(splitPrompt);
    }

    // Arch prompts
    // Edge 2->4 (snappedEdges[0])
    if (!skipLegAtCorner2) {
      const midpoint_2_4 = new THREE.Vector3().addVectors(corner2, corner4).multiplyScalar(0.5);
      const prompt_2_4: LinePrompt = {
        id: nextIdFn(),
        worldPoint: midpoint_2_4,
        direction: perpDir.clone(),
        screenPosition: this.coordinateTransformer.worldToScreen(midpoint_2_4),
        positiveLength: perpLength / 2,
        negativeLength: perpLength / 2,
        isTemporary: isTemporary ? true : undefined,
      };
      this.applySnapToPrompt(prompt_2_4, 0, resolvedEdges, densityMap, positionMap, floorHeight);
      newPrompts.push(prompt_2_4);
    }

    // Edge 4->3 (snappedEdges[1]) - always created
    const midpoint_4_3 = new THREE.Vector3().addVectors(corner4, corner3).multiplyScalar(0.5);
    const prompt_4_3: LinePrompt = {
      id: nextIdFn(),
      worldPoint: midpoint_4_3,
      direction: promptDir.clone(),
      screenPosition: this.coordinateTransformer.worldToScreen(midpoint_4_3),
      positiveLength: sideLength / 2,
      negativeLength: sideLength / 2,
      isTemporary: isTemporary ? true : undefined,
    };
    this.applySnapToPrompt(prompt_4_3, 1, resolvedEdges, densityMap, positionMap, floorHeight);
    newPrompts.push(prompt_4_3);

    // Edge 3->1 (snappedEdges[2])
    if (!skipLegAtCorner1) {
      const midpoint_3_1 = new THREE.Vector3().addVectors(corner3, corner1).multiplyScalar(0.5);
      const prompt_3_1: LinePrompt = {
        id: nextIdFn(),
        worldPoint: midpoint_3_1,
        direction: perpDir.clone(),
        screenPosition: this.coordinateTransformer.worldToScreen(midpoint_3_1),
        positiveLength: perpLength / 2,
        negativeLength: perpLength / 2,
        isTemporary: isTemporary ? true : undefined,
      };
      this.applySnapToPrompt(prompt_3_1, 2, resolvedEdges, densityMap, positionMap, floorHeight);
      newPrompts.push(prompt_3_1);
    }

    // When a leg was collapsed (skipped due to collinear neighbor at the prompt end),
    // adjust the pre-existing neighbor so it can connect with the arch.
    if (skipLegAtCorner1) {
      const neighbor = corner1IsMin ? negNeighbor : posNeighbor;
      if (neighbor) {
        this.adjustNeighborForCollapsedLeg(prompt_4_3, neighbor, startPoint);
      }
    }
    if (skipLegAtCorner2) {
      const neighbor = corner1IsMin ? posNeighbor : negNeighbor;
      if (neighbor) {
        this.adjustNeighborForCollapsedLeg(prompt_4_3, neighbor, startPoint);
      }
    }

    return { newPrompts, originalPromptIndex: originalIndex };
  }

  /**
   * Adjust a pre-existing neighbor prompt when its collinear leg was collapsed.
   * Intersects the arch's infinite line with the neighbor's infinite line,
   * then places the neighbor's worldPoint at the midpoint of the intersection
   * and the neighbor's endpoint farthest from the drag origin.
   */
  private adjustNeighborForCollapsedLeg(archPrompt: LinePrompt, neighbor: LinePrompt, dragOrigin: THREE.Vector3): void {
    const hit = this.intersectPerpendicularLines(archPrompt.worldPoint, archPrompt.direction, neighbor.worldPoint, neighbor.direction);
    if (!hit) return;

    // Pick the neighbor endpoint farthest from the drag origin
    const posLen = neighbor.positiveLength ?? 1.5;
    const negLen = neighbor.negativeLength ?? 1.5;
    const posEnd = neighbor.worldPoint.clone().add(neighbor.direction.clone().multiplyScalar(posLen));
    const negEnd = neighbor.worldPoint.clone().add(neighbor.direction.clone().multiplyScalar(-negLen));
    const farthest = dragOrigin.distanceTo(posEnd) >= dragOrigin.distanceTo(negEnd) ? posEnd : negEnd;

    // Place worldPoint at midpoint of intersection and farthest endpoint
    neighbor.worldPoint.copy(new THREE.Vector3().addVectors(hit, farthest).multiplyScalar(0.5));
    neighbor.screenPosition = this.coordinateTransformer.worldToScreen(neighbor.worldPoint);
  }

  /**
   * Apply density-snapped positions to a newly created prompt.
   * If snappedEdges are available (from the drag preview), uses those positions directly
   * so the final result matches what the user saw in green.
   * Otherwise falls back to running density alignment on the prompt.
   */
  private applySnapToPrompt(prompt: LinePrompt, edgeIndex: number, snappedEdges: Array<[THREE.Vector3, THREE.Vector3]> | null, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null): void {
    if (snappedEdges && snappedEdges.length === 3) {
      const [edgeStart, edgeEnd] = snappedEdges[edgeIndex];
      prompt.worldPoint.copy(new THREE.Vector3().addVectors(edgeStart, edgeEnd).multiplyScalar(0.5));
      // Derive lengths from snapped edge endpoints (the green preview lines),
      // not from user input, so the final prompts match the preview exactly
      const snappedHalfLength = edgeStart.distanceTo(edgeEnd) / 2;
      prompt.positiveLength = snappedHalfLength;
      prompt.negativeLength = snappedHalfLength;
    } else if (densityMap) {
      this.densitySnapper.alignPromptToPosition(prompt, densityMap, positionMap, floorHeight);
    }
    prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
  }

  /**
   * Apply density alignment to a split prompt (no pre-computed snap, runs alignment directly).
   */
  private alignSplitPrompt(prompt: LinePrompt, densityMap: ImageData | null, positionMap: ImageData | null, floorHeight: number | null): void {
    if (densityMap) {
      this.densitySnapper.alignPromptToPosition(prompt, densityMap, positionMap, floorHeight);
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
    }
  }

  /**
   * Snap a single edge by creating a temporary prompt at the midpoint and aligning it.
   * Returns the snapped edge endpoints.
   */
  private snapEdge(cornerA: THREE.Vector3, cornerB: THREE.Vector3, length: number, densityMap: ImageData, positionMap: ImageData | null, floorHeight: number | null): [THREE.Vector3, THREE.Vector3] {
    const midpoint = new THREE.Vector3().addVectors(cornerA, cornerB).multiplyScalar(0.5);
    // Use the actual A→B direction to preserve endpoint ordering after snap
    const edgeDir = new THREE.Vector3().subVectors(cornerB, cornerA).normalize();
    const tempPrompt: LinePrompt = {
      id: '_snap_preview',
      worldPoint: midpoint.clone(),
      direction: edgeDir.clone(),
      screenPosition: new THREE.Vector2(0, 0),
      positiveLength: length / 2,
      negativeLength: length / 2,
    };
    this.densitySnapper.alignPromptToPosition(tempPrompt, densityMap, positionMap, floorHeight);
    const halfDir = edgeDir.clone().multiplyScalar(length / 2);
    // [0] corresponds to cornerA side, [1] to cornerB side
    return [tempPrompt.worldPoint.clone().sub(halfDir), tempPrompt.worldPoint.clone().add(halfDir)];
  }
}
